import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BatchFileState, LocalFile, UploadPhase } from './types';
import {
  computeSha256,
  FILE_IN_USE_MESSAGE,
  getExtension,
  inferContentType,
  MAX_FILES_PER_BATCH,
  validateFile
} from './utils';
import { useHubUpload, HubUploadStage, UploadTargetContext } from './useHubUpload';

// ---------------------------------------------------------------------------
// localStorage tracking
//
// Finished batches are persisted so the progress widget can show "Upload
// complete" after a reload. In-progress snapshots are not: the upload dies
// with the page (no batch id is stored, so nothing can poll), and restoring
// them pins the widget at 0% with no way to dismiss it.
// ---------------------------------------------------------------------------

const TRACKING_STORAGE_KEY = 'upload_batch_tracking';

interface PersistedTracking {
  batchFiles: BatchFileState[];
}

function saveTracking(batchFiles: BatchFileState[]): void {
  try {
    localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify({ batchFiles }));
  } catch {
    // Storage may be unavailable (private mode, quota). The widget still works
    // for the current page.
  }
}

export function clearTracking(): void {
  try {
    localStorage.removeItem(TRACKING_STORAGE_KEY);
  } catch {
    // Ignore — see saveTracking.
  }
}

function loadTracking(): PersistedTracking | null {
  try {
    const raw = localStorage.getItem(TRACKING_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedTracking;
  } catch {
    clearTracking();
    return null;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map hub pipeline stage → BatchFileState status string. */
const STAGE_TO_STATUS: Record<HubUploadStage, BatchFileState['status']> = {
  uploading: 'pending_upload',
  scanning: 'security_scanning',
  processing: 'clean',
  ready: 'ready',
  error: 'failed'
};

const TERMINAL_STATUSES = new Set(['ready', 'infected', 'rejected', 'scan_failed', 'failed']);

function isAllTerminal(files: BatchFileState[]): boolean {
  return files.length > 0 && files.every((file) => TERMINAL_STATUSES.has(file.status));
}

/**
 * Restore a finished batch for the progress widget.
 *
 * Anything still in progress is discarded. Reloading cannot resume it, and
 * showing the frozen snapshot leaves "Processing files…" stuck at 0%.
 */
function loadRestorableTracking(): BatchFileState[] {
  const loaded = loadTracking();
  const files = loaded?.batchFiles;
  if (!Array.isArray(files) || !isAllTerminal(files)) {
    if (loaded) clearTracking();
    return [];
  }
  return files;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UseUploadBatchResult {
  phase: UploadPhase;
  localFiles: LocalFile[];
  batchId: string | null;
  batchFiles: BatchFileState[];
  addFiles: (files: File[]) => Promise<void>;
  removeLocalFile: (key: string) => void;
  startUpload: () => void;
  stopUpload: () => void;
  reset: () => void;
  clearTracking: () => void;
  /** Hide the progress widget and forget any batch restored from storage. */
  dismissTracking: () => void;
  dismissBatchFile: (key: string) => void;
  uploading: boolean;
  uploadError: string | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useUploadBatch(
  documentType?: string,
  target?: UploadTargetContext,
  skipDI = true
): UseUploadBatchResult {
  const [restoredFiles, setRestoredFiles] = useState<BatchFileState[]>(loadRestorableTracking);

  const [phase, setPhase] = useState<UploadPhase>(restoredFiles.length > 0 ? 'tracking' : 'selecting');
  const [localFiles, setLocalFiles] = useState<LocalFile[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Hub keys returned by uploadFiles(), in the same order as the files passed in.
  // Used to map hub state back to local file keys.
  const hubKeysRef = useRef<Array<{ hubKey: string; localKey: string; name: string; size: number }>>([]);

  const hub = useHubUpload({
    documentType,
    skipDI: () => skipDI
  });

  // ---------------------------------------------------------------------------
  // Derive batchFiles from hub.uploads (reactive — re-runs on every hub state change)
  // ---------------------------------------------------------------------------

  const derivedBatchFiles = useMemo<BatchFileState[]>(() => {
    if (hubKeysRef.current.length === 0) return [];
    return hubKeysRef.current.map(({ hubKey, localKey, name, size }) => {
      const hs = hub.uploads.get(hubKey);
      return {
        fileId: hs?.documentId ?? localKey,
        key: localKey,
        name: hs?.file.name ?? name,
        size: hs?.file.size ?? size,
        status: hs ? STAGE_TO_STATUS[hs.stage] : 'failed',
        uploadProgress: hs?.stage === 'uploading' ? hs.uploadProgress : 100,
        errorMessage: hs?.error
      };
    });
    // hub.uploads is a Map — its reference changes on every update, so the
    // memo correctly re-runs whenever any file's stage or progress changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hub.uploads]);

  // Restored files are only a fallback for the progress widget after reload.
  // Once this hook is uploading, hub-derived state takes over.
  const batchFiles = derivedBatchFiles.length > 0 ? derivedBatchFiles : restoredFiles;

  // Persist only finished batches. An in-progress snapshot cannot be resumed
  // after reload and would pin the widget at 0%.
  useEffect(() => {
    if (derivedBatchFiles.length === 0) return;
    if (isAllTerminal(derivedBatchFiles)) {
      saveTracking(derivedBatchFiles);
      return;
    }
    clearTracking();
  }, [derivedBatchFiles]);

  // Transition to 'tracking' once every file has reached a terminal state
  useEffect(() => {
    if (phase !== 'uploading') return;
    if (derivedBatchFiles.length === 0) return;
    const allDone = derivedBatchFiles.every((f) => TERMINAL_STATUSES.has(f.status));
    if (allDone) {
      setPhase('tracking');
    }
  }, [derivedBatchFiles, phase]);

  // ---------------------------------------------------------------------------
  // Local file selection
  // ---------------------------------------------------------------------------

  const addFiles = useCallback(
    async (files: File[]) => {
      const sha256Set = new Set(localFiles.map((f) => f.sha256).filter(Boolean) as string[]);

      const incoming: LocalFile[] = [];
      for (const file of files) {
        if (getExtension(file.name) === 'zip') continue;
        const key = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
        const validationError = validateFile(file, sha256Set);
        incoming.push({
          key,
          file,
          name: file.name,
          size: file.size,
          contentType: inferContentType(file),
          validationError
        });
      }

      const hashed = await Promise.all(
        incoming.map(async (entry) => {
          if (entry.validationError) return entry;
          try {
            const sha256 = await computeSha256(entry.file);
            if (sha256Set.has(sha256)) return { ...entry, sha256, validationError: 'duplicate' as const };
            sha256Set.add(sha256);
            return { ...entry, sha256 };
          } catch (err: unknown) {
            setUploadError(err instanceof Error ? err.message : FILE_IN_USE_MESSAGE);
            return null;
          }
        })
      );
      const withHashes = hashed.filter((entry): entry is LocalFile => entry != null);

      setLocalFiles((prev) => [...prev, ...withHashes].slice(0, MAX_FILES_PER_BATCH));
    },
    [localFiles]
  );

  const removeLocalFile = useCallback((key: string) => {
    setLocalFiles((prev) => prev.filter((f) => f.key !== key));
  }, []);

  // ---------------------------------------------------------------------------
  // Upload
  // ---------------------------------------------------------------------------

  const startUpload = useCallback(() => {
    const valid = localFiles.filter((f) => !f.validationError && f.sha256);
    if (valid.length === 0) return;

    setUploadError(null);
    setPhase('uploading');

    try {
      // uploadFiles() is synchronous (fires the pipeline and returns keys)
      const hubKeys = hub.uploadFiles(
        valid.map((f) => f.file),
        target
      );

      // Record the mapping hub-key → local-key for the derived state above
      hubKeysRef.current = hubKeys.map((hubKey, i) => ({
        hubKey,
        localKey: valid[i].key,
        name: valid[i].name,
        size: valid[i].size
      }));
    } catch (err: any) {
      setUploadError(err?.response?.data?.detail ?? err?.message ?? 'Upload failed');
      setPhase('selecting');
    }
  }, [localFiles, hub, target]);

  // ---------------------------------------------------------------------------
  // Reset
  // ---------------------------------------------------------------------------

  const stopUpload = useCallback(() => {
    hub.clearUploads();
    hubKeysRef.current = [];
    clearTracking();
    setRestoredFiles([]);
    setPhase('selecting');
    // Keep localFiles so the user can review or re-upload the same selection.
  }, [hub]);

  const reset = useCallback(() => {
    hub.clearUploads();
    hubKeysRef.current = [];
    clearTracking();
    setRestoredFiles([]);
    setPhase('selecting');
    setLocalFiles([]);
    setUploadError(null);
  }, [hub]);

  const dismissBatchFile = useCallback(
    (key: string) => {
      const match = hubKeysRef.current.find((entry) => entry.localKey === key);
      if (match) hub.removeFile(match.hubKey);
      hubKeysRef.current = hubKeysRef.current.filter((entry) => entry.localKey !== key);
      setRestoredFiles((current) => {
        const next = current.filter((file) => file.key !== key && file.fileId !== key);
        if (next.length === 0) clearTracking();
        else saveTracking(next);
        return next;
      });
    },
    [hub]
  );

  const dismissTracking = useCallback(() => {
    hub.clearUploads();
    hubKeysRef.current = [];
    clearTracking();
    setRestoredFiles([]);
    setPhase('selecting');
    setUploadError(null);
  }, [hub]);

  // uploading = blobs still being sent or pipeline still running
  const uploading = phase === 'uploading';

  return {
    phase,
    localFiles,
    batchId: null,
    batchFiles,
    addFiles,
    removeLocalFile,
    startUpload,
    stopUpload,
    reset,
    clearTracking,
    dismissTracking,
    dismissBatchFile,
    uploading,
    uploadError
  };
}
